import React from 'react'
import styled from '@emotion/styled'
import { Navigate } from 'react-router'
import { Button } from '@mobi/component-library/Base/Buttons'
import { measurements, spacing } from '@mobi/component-library/Foundations'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { InfoHeader } from '@mobi/component-library/Typography/InfoHeader'
import { sportsOfferingQueryKeys } from '@mobi/sports-offering/helpers/queries/keys'
import { AppRoutes } from '@core/AppRoutes'
import { useForceAppHeaderFixed } from '@core/Areas/AppHeader/hooks/useForceAppHeaderFixed'
import { useKambiMaintenanceFeature } from '@core/Areas/KambiClient/Components/KambiRouteEffects/hooks'
import { KAMBI_CONTAINER_BG_COLOR } from '@core/Areas/KambiClient/constants'
import { queryClient } from '@core/Data/ReactQuery'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { appHistory } from '@core/Utils/routing/history'
import { useNewSportsOfferingFeature } from './hooks/useNewSportsOfferingFeature'
import { useToggleKambiBetSlipVisibility } from './hooks/useToggleKambiBetSlipVisibility'

const SportsOfferingLazyRoute = React.lazy(() => import('./Components/MainSportsOffering'))

export const SportOfferingRoot: React.FC = () => {
  const isKambiSportsOfferingEnabled = useNewSportsOfferingFeature()
  useToggleKambiBetSlipVisibility(isKambiSportsOfferingEnabled)
  const maintenanceEndDate = useKambiMaintenanceFeature()
  useForceAppHeaderFixed()

  React.useEffect(() => {
    if (maintenanceEndDate) {
      queryClient.invalidateQueries({
        queryKey: sportsOfferingQueryKeys.SportsOfferingBase,
        refetchType: 'all',
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.kambiPlayerBase,
        refetchType: 'all',
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.kambiShapeGamesBase,
        refetchType: 'all',
      })
    }
  }, [maintenanceEndDate])

  if (maintenanceEndDate) {
    return (
      <ContainerStyled>
        <InfoHeader
          icon='info'
          header='Sports is Unavailable Due to Maintenance'
          subHeader={
            <Flex flexDirection='column' gap={spacing.sm}>
              <span>
                We expect to be back up and running by{' '}
                {new Date(maintenanceEndDate).toLocaleString('en-AU', {
                  weekday: 'long',
                  hour: 'numeric',
                  minute: 'numeric',
                })}
              </span>
              <span>Our customer service centre can be contacted on 1300 36 36 88</span>
              <Button color='primary' onClick={() => appHistory.push(AppRoutes.Home)}>
                Return to Home Page
              </Button>
            </Flex>
          }
        />
      </ContainerStyled>
    )
  }

  if (!isKambiSportsOfferingEnabled) {
    return <Navigate to={AppRoutes.SportsRoot + '/home'} replace />
  }

  return (
    <React.Suspense fallback={null}>
      <SportsOfferingLazyRoute />
    </React.Suspense>
  )
}

// ======
// Styles
// ======

const ContainerStyled = styled.div({
  boxSizing: 'border-box',
  width: '100%',
  minHeight: `calc(100vh - ${measurements.mobi.headerHeight})`,
  padding: spacing.sm,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: KAMBI_CONTAINER_BG_COLOR,
  fontSize: '4rem',
})
